import React, { useState, type FC, useEffect } from 'react';
import { type FormInstance } from 'antd';
import { Button, InputText, Space } from '@g2u/ui-kit';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';
import cx from 'classnames';

import { AddressType, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { getAddressPrediction, getAddressPredictionDetails, getSuburb } from 'queries';
import { AddressLookup, Section } from 'components';
import { Toast, parseErrors } from 'helpers';
import styles from './home-address-fields.module.scss';

export namespace HomeAddressFields {
  export type Props = {
    inline?: boolean;
    form: FormInstance;
    customer: JsonDataItem | null;
    location?: JsonDataItem | null;
    isTechnicianMode?: boolean;
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const { inline, customer, form, location, isTechnicianMode } = props;
    const [isManualEditing, setIsManualEditing] = useState<boolean>(false);
    const isEmptyFieldsHidden = !customer?.physical_address_line1 && !isManualEditing && isTechnicianMode;

    // Address lookup: get prediction list
    const [predictionError, setPredictionError] = useState<string>();
    const { mutate: predictAddress, data: predictedHomeAddress } = useMutation(getAddressPrediction, {
      onSuccess: (data) => {
        setPredictionError(data?.messages?.[0]);
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });
    useEffect(() => {
      predictionError &&
        Toast.show({
          description: predictedHomeAddress?.messages?.[0],
          type: Toast.Type.Error,
        });
    }, [predictionError]);

    // Address lookup: select prediction and get details
    const handlePredictionSelect = (option: JsonDataItem) => {
      const label = option.label.substring(0, option.label.indexOf(','));
      form.setFieldValue('homeAddressLine1', label);
      getPredictionDetails({ id: option.value });
    };
    const { mutate: getPredictionDetails } = useMutation(getAddressPredictionDetails, {
      onSuccess: (data: JsonDataItem) => {
        const addressLine1 = data.address.properties.formatted_address.substring(
          0,
          data.address.properties.formatted_address.indexOf(','),
        );
        const suburb = data.address.properties.locality_name;
        const state = data.address.properties.state_territory;
        const postcode = data.address.properties.postcode;

        // new customer location matching with geek selection
        if (
          !customer &&
          location &&
          (form.getFieldValue('homeAddressSuburb') !== suburb ||
            form.getFieldValue('homeAddressPostcode') !== Number(postcode))
        ) {
          Toast.show({
            // eslint-disable-next-line quotes
            description: "New address doesn't match the selected technician location",
            type: Toast.Type.Error,
          });
          form.setFieldValue('homeAddressLine1', undefined);
        } else {
          form.setFieldsValue({
            homeAddressLine1: addressLine1,
            homeAddressSuburb: suburb,
            homeAddressState: state,
            homeAddressPostcode: postcode,
          });
        }
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    // Suburb lookup: get a list
    const { mutate: suburbLookup, data: suggestedSuburbs } = useMutation(getSuburb, {
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    // Suburb lookup: set selection to dependend fields
    const handleSuburbSelect = (option: JsonDataItem) => {
      const selectedSuburb = suggestedSuburbs.find((suggestion: JsonDataItem) => suggestion.ID === option.value);
      form.setFieldsValue({
        homeAddressSuburb: selectedSuburb.suburb,
        homeAddressState: selectedSuburb.state,
        homeAddressPostcode: selectedSuburb.postcode,
      });
    };
    const handleEditingMode = () => {
      // reset fields after manual editing
      if (isManualEditing) {
        form.setFieldValue(
          'postalAddressLine1',
          customer?.postal_address_line1 || customer?.postal_address.postal_address_line_1,
        );
        form.setFieldValue('postalAddressSuburb', customer?.postal_suburb || customer?.postal_address.suburb);
        form.setFieldValue('postalAddressState', customer?.postal_state || customer?.postal_address.state);
        form.setFieldValue('postalAddressPostcode', customer?.postal_postcode || customer?.postal_address.postcode);
      }
      setIsManualEditing(!isManualEditing);
    };

    return (
      <Section.$
        title={isTechnicianMode ? '' : 'Address'}
        inline={inline}
        className={cx(isTechnicianMode && styles.homeAddress_techMode)}
      >
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} className={styles.homeAddress__address}>
          <Space.$
            direction={Space.Direction.Horizontal}
            size={Space.Size.Middle}
            className={styles.homeAddress__addressStreet1}
          >
            <Space.$
              direction={Space.Direction.Horizontal}
              compact
              fullWidth
              align={Space.Align.End}
              style={{ alignItems: 'flex-end', position: 'relative' }}
            >
              <AddressLookup.$
                name="homeAddressLine1"
                placeholder={'Street Address Line 1'}
                onAddressSearch={(value: string) => {
                  predictAddress({ value, type: AddressType.Physical });
                }}
                onAddressSelect={handlePredictionSelect}
                initialValue={customer?.address.address_line_1}
                label={isTechnicianMode ? 'Physical Address: ' : 'Street Address:'}
                addressOptions={predictedHomeAddress?.suggest?.map((item: JsonDataItem) => {
                  return { label: item.address, value: item.id };
                })}
                className={styles.homeAddress__formItem_vertical}
                dataTestId="home-address-line-1"
                isManual={isManualEditing}
              />
              <Button.$
                onClick={handleEditingMode}
                dataTestId="edit-switcher"
                style={{ position: 'absolute', left: '100%', marginLeft: -1, top: isTechnicianMode ? 0 : 'auto' }}
              >
                {isManualEditing ? (
                  <>
                    <SearchOutlined /> Use lookup
                  </>
                ) : (
                  <>
                    <EditOutlined /> Enter manually
                  </>
                )}
              </Button.$>
            </Space.$>
          </Space.$>
          {!isEmptyFieldsHidden && (
            <>
              <Space.$ fullWidth stretch className={styles.homeAddress__addressStreet2}>
                <InputText.$
                  dataTestId="address-line-2"
                  name="homeAddressLine2"
                  className={styles.homeAddress__formItem_vertical}
                  fullWidth
                  placeholder="Street Address Line 2"
                  initialValue={customer?.address.address_line_2}
                  label={isTechnicianMode ? ' ' : undefined}
                  disabled={isTechnicianMode && !isManualEditing}
                />
              </Space.$>
              <Space.$
                size={Space.Size.Middle}
                className={styles.homeAddress__addressSuburbDetails}
                direction={Space.Direction.Vertical}
              >
                <Space.$
                  direction={Space.Direction.Horizontal}
                  size={Space.Size.Middle}
                  className={styles.homeAddress__addressSuburb}
                >
                  <AddressLookup.$
                    name="homeAddressSuburb"
                    placeholder={'Suburb'}
                    onAddressSearch={(value: string) => {
                      suburbLookup({ value });
                    }}
                    onAddressSelect={handleSuburbSelect}
                    initialValue={customer?.address.suburb || location?.suburb}
                    label={isTechnicianMode ? ' ' : 'Suburb and State:'}
                    addressOptions={suggestedSuburbs?.map((item: JsonDataItem) => {
                      return { label: `${item.suburb}, ${item.postcode}`, value: item.ID };
                    })}
                    className={styles.homeAddress__formItem_vertical}
                    dataTestId="home-suburb"
                    readonly={!isManualEditing}
                  />
                </Space.$>
                <Space.$
                  direction={Space.Direction.Horizontal}
                  size={Space.Size.Middle}
                  className={styles.homeAddress__addressSuburb}
                >
                  <InputText.$
                    dataTestId="home-address-state"
                    name="homeAddressState"
                    className={cx(styles.homeAddress__formItem_vertical, styles.homeAddress__addressState)}
                    fullWidth
                    label=" "
                    placeholder="State"
                    initialValue={customer?.address.state || location?.state}
                    readonly
                    disabled={isTechnicianMode}
                  />
                  <InputText.$
                    dataTestId="home-address-postcode"
                    name="homeAddressPostcode"
                    className={cx(styles.homeAddress__formItem_vertical, styles.homeAddress__addressPostcode)}
                    fullWidth
                    label=" "
                    placeholder="Postcode"
                    initialValue={customer?.address.postcode || location?.postcode}
                    readonly
                    disabled={isTechnicianMode}
                  />
                </Space.$>
              </Space.$>
            </>
          )}
        </Space.$>
      </Section.$>
    );
  };
}
